import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '../../config/supabase';
import { traderService } from '../../services/traderService';
import { craftsmanService } from '../../services/craftsmanService';
import { triggerKeywordGeneration, getBusinessTypeFromTable } from '../../services/keywordService';
import { Loader2, Upload, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import slugify from '../../utils/slugify';
import { compressImage, AVATAR_PRESET, validateImageFile, formatFileSize } from '../../utils/imageCompression';
import { normalizePhoneNumber, normalizeWebsiteUrl, isValidPhoneNumber, validateWebsite } from '../../utils/inputValidation';

const PASSWORD_MIN_LENGTH = 8;

// Form data interface matching the existing database structure
interface TraderFormData {
  email: string;
  password: string;
  confirmPassword: string;
  store_name: string; // Maps to business_name in UI
  owner_name: string;
  phone_number: string;
  location: string;
  description: string;
  business_type_id: string; // Business type UUID
  profession_id: string; // Profession integer ID (id_no)
  website?: string; // Optional field
}

// Step component props interface
interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  title: string;
}

// Progress indicator component
const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, totalSteps, title }) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-center mb-4">
        {Array.from({ length: totalSteps }, (_, index) => (
          <React.Fragment key={index}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index + 1 < currentStep
                  ? 'bg-green-500 text-white'
                  : index + 1 === currentStep
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {index + 1 < currentStep ? (
                <Check className="w-4 h-4" />
              ) : (
                index + 1
              )}
            </div>
            {index < totalSteps - 1 && (
              <div
                className={`w-12 h-1 mx-2 ${
                  index + 1 < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      <h2 className="text-xl font-semibold text-center text-gray-800">{title}</h2>
    </div>
  );
};

const generateUniqueHospitalitySlug = async (storeName: string, excludeId?: number | string): Promise<string> => {
  const base = slugify(storeName) || 'hospitality';
  let candidate = base;
  let suffix = 2;

  while (true) {
    let query = supabase
      .from('hospitality')
      .select('id')
      .eq('slug', candidate)
      .limit(1);

    if (excludeId !== undefined && excludeId !== null) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error checking hospitality slug uniqueness:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return candidate;
    }

    candidate = `${base}-${suffix++}`;
  }
};

const generateUniqueProfessionalSlug = async (storeName: string, excludeId?: number | string): Promise<string> => {
  const base = slugify(storeName) || 'professional';
  let candidate = base;
  let suffix = 2;

  while (true) {
    let query = supabase
      .from('professionals')
      .select('id')
      .eq('slug', candidate)
      .limit(1);

    if (excludeId !== undefined && excludeId !== null) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error checking professionals slug uniqueness:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return candidate;
    }

    candidate = `${base}-${suffix++}`;
  }
};

export default function MultiStepTraderRegistration() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isCompressing, setIsCompressing] = useState(false); // Track image compression state
  const [compressionInfo, setCompressionInfo] = useState<string>(''); // Show compression result
  const [businessTypes, setBusinessTypes] = useState<{ id: string; name: string }[]>([]);
  const [professions, setProfessions] = useState<{ id: number; name: string }[]>([]);
  const [hospitalityCategories, setHospitalityCategories] = useState<{ id: number; name: string }[]>([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>('');
  const [selectedBusinessTypeName, setSelectedBusinessTypeName] = useState<string>('');
  // Type A trade categories for tradesman registrations
  const traderCategories = [
    'Building & Construction',
    'Interiors & Renovation',
    'Plumbing & Heating',
    'Home Maintenance',
    'Garden & Outdoors',
    'Cleaning & Logistics',
    'Motors',
    'Health & Lifestyle',
    'Technology & IT',
    'Creative & Media',
  ];
  const [selectedTraderCategory, setSelectedTraderCategory] = useState<string>('');
  const [showOtherProfessionInput, setShowOtherProfessionInput] = useState<boolean>(false);
  const [otherProfession, setOtherProfession] = useState<string>('');
  const [isSubmittingProfession, setIsSubmittingProfession] = useState<boolean>(false);
  const [professionError, setProfessionError] = useState<string>('');
  const [countryCode, setCountryCode] = useState('+353'); // Default to Ireland
  const [phoneNumber, setPhoneNumber] = useState('');
  const [focusedField, setFocusedField] = useState<keyof TraderFormData | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    getFieldState,
    watch,
  } = useForm<TraderFormData>({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      store_name: '',
      owner_name: '',
      email: '',
      password: '',
      confirmPassword: '',
      business_type_id: '',
      profession_id: '',
      location: '',
      phone_number: '',
      description: '',
      website: '',
    },
  });

  const totalSteps = 3;

  // Helper to check if a specific field has been touched and is currently valid.
  // This is used to show a small green check icon next to correctly filled inputs.
  const isFieldValid = (name: keyof TraderFormData): boolean => {
    const fieldState = getFieldState(name);
    const value = getValues(name);

    // Treat empty values as not valid for the purpose of showing the tick.
    const hasValue =
      typeof value === 'string'
        ? value.trim() !== ''
        : value !== undefined && value !== null;

    // Only show tick when field is not currently focused, has been touched,
    // is valid, and has a non-empty value.
    return fieldState.isTouched && !fieldState.invalid && hasValue && focusedField !== name;
  };

  // ===== Success sound for valid fields =====
  // We use a shared audio instance and track, per field, whether the "valid" ping
  // has already been played. This avoids spamming sound on every keypress.
  const successSoundRef = React.useRef<HTMLAudioElement | null>(null);
  const fieldSoundPlayedRef = React.useRef<Record<string, boolean>>({});

  // Lazily create the audio element on the client.
  React.useEffect(() => {
    successSoundRef.current = new Audio('/ping-82822.mp3');
    if (successSoundRef.current) {
      successSoundRef.current.volume = 0.4;
    }
  }, []);

  // Watch all form values so we can react when fields change.
  const watchedValues = watch();

  React.useEffect(() => {
    if (!successSoundRef.current) return;

    const fields: (keyof TraderFormData)[] = [
      'store_name',
      'owner_name',
      'email',
      'password',
      'confirmPassword',
      'business_type_id',
      'profession_id',
      'location',
      'phone_number',
      'description',
      'website',
    ];

    fields.forEach((name) => {
      const { isTouched, invalid } = getFieldState(name);
      const key = String(name);
      const alreadyPlayed = fieldSoundPlayedRef.current[key];

      const rawValue = (watchedValues as any)[name];
      const hasValue =
        typeof rawValue === 'string'
          ? rawValue.trim() !== ''
          : rawValue !== undefined && rawValue !== null;

      const isCurrentlyFocused = focusedField === name;

      // Play sound once when field transitions into a valid, touched state.
      // We also require that the field is not currently focused, so the
      // sound fires after the user leaves the field with a correct value.
      if (isTouched && hasValue && !invalid && !alreadyPlayed && !isCurrentlyFocused) {
        try {
          successSoundRef.current!.currentTime = 0;
          void successSoundRef.current!.play();
        } catch {
          // Ignore play errors (e.g. autoplay restrictions)
        }
        fieldSoundPlayedRef.current[key] = true;
      }

      // If field becomes invalid again, allow the sound to play next time it recovers.
      if ((!isTouched || invalid) && alreadyPlayed) {
        fieldSoundPlayedRef.current[key] = false;
      }
    });
  }, [watchedValues, getFieldState]);

  // Step titles
  const stepTitles = [
    'Basic Information',
    'Business Details',
    'Company Profile',
  ];

  // Fetch business types on component mount
  React.useEffect(() => {
    const fetchBusinessTypes = async () => {
      try {
        const { data: typesData, error: typesError } = await supabase
          .from('business_types')
          .select('id, name')
          .order('name');

        if (typesError) {
          console.error('Error fetching business types:', typesError);
        } else {
          setBusinessTypes(typesData || []);
          console.log('Fetched business types:', typesData);
        }
      } catch (err) {
        console.error('Error fetching business types:', err);
      }
    };

    fetchBusinessTypes();
  }, []);

  // Fetch hospitality categories on component mount
  useEffect(() => {
    const fetchHospitalityCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('hospitality_categories')
          .select('id, name')
          .order('name');

        if (error) {
          console.error('Error fetching hospitality categories:', error);
        } else {
          setHospitalityCategories(data || []);
          console.log('Fetched hospitality categories:', data);
        }
      } catch (err) {
        console.error('Error fetching hospitality categories:', err);
      }
    };

    fetchHospitalityCategories();
  }, []);

  // Fetch professions when business type changes
  React.useEffect(() => {
    const fetchProfessions = async () => {
      if (!selectedBusinessType) {
        setProfessions([]);
        return;
      }

      try {
        // Find the selected business type name
        const businessType = businessTypes.find(bt => bt.id === selectedBusinessType);
        const businessTypeName = businessType?.name?.toLowerCase();
        setSelectedBusinessTypeName(businessTypeName || '');

        let tableName = '';
        if (businessTypeName === 'tradesman') {
          tableName = 'professions';
          // Require a trade category to narrow down the professions list
          if (!selectedTraderCategory) {
            setProfessions([]);
            return;
          }
        } else if (businessTypeName === 'craftsman') {
          tableName = 'craft_professions';
        } else if (businessTypeName === 'hospitality') {
          // For hospitality, we'll use the already loaded categories
          setProfessions(hospitalityCategories);
          return;
        } else if (businessTypeName && businessTypeName.includes('professional')) {
          tableName = 'professionals_categories';
        } else {
          // For other business types, don't show professions
          setProfessions([]);
          return;
        }

        let query = supabase
          .from(tableName)
          .select('id, name')
          .order('name');

        // For tradesman, filter professions by the selected Type A category
        if (tableName === 'professions' && selectedTraderCategory) {
          query = query.eq('category', selectedTraderCategory);
        }

        const { data: professionsData, error: professionsError } = await query;

        if (professionsError) {
          console.error(`Error fetching ${tableName}:`, professionsError);
          setProfessions([]);
        } else {
          setProfessions(professionsData || []);
          console.log(`Fetched ${tableName}:`, professionsData);
        }
      } catch (err) {
        console.error('Error fetching professions:', err);
        setProfessions([]);
      }
    };

    fetchProfessions();
  }, [selectedBusinessType, businessTypes, hospitalityCategories, selectedTraderCategory]);

  // Handle business type change
  const handleBusinessTypeChange = (value: string) => {
    setSelectedBusinessType(value);
    // Reset profession selection when business type changes
    setValue('profession_id', '');
    setShowOtherProfessionInput(false);
    setOtherProfession('');
    setProfessionError('');
    setSelectedTraderCategory('');
    
    // Store the business type name for later use
    const businessType = businessTypes.find(bt => bt.id === value);
    setSelectedBusinessTypeName(businessType?.name?.toLowerCase() || '');
  };

  // Handle trade category (Type A) change for tradesman
  const handleTraderCategoryChange = (value: string) => {
    setSelectedTraderCategory(value);
    // Reset profession selection when category changes
    setValue('profession_id', '');
    setShowOtherProfessionInput(false);
    setOtherProfession('');
    setProfessionError('');
  };

  // Handle profession selection change
  const handleProfessionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setValue('profession_id', value);
    setShowOtherProfessionInput(value === 'other');
    setOtherProfession('');
    setProfessionError('');
  };

  // Handle adding a new profession
  const handleAddNewProfession = async () => {
    if (!otherProfession.trim()) {
      setProfessionError('Please enter a profession name');
      return;
    }

    if (!selectedBusinessType) {
      setProfessionError('Please select a business type first');
      return;
    }

    setIsSubmittingProfession(true);
    setProfessionError('');

    try {
      let tableName = '';
      const businessType = businessTypes.find(bt => bt.id === selectedBusinessType);
      const businessTypeName = businessType?.name?.toLowerCase() || '';

      if (businessTypeName === 'tradesman') {
        tableName = 'professions';
      } else if (businessTypeName === 'craftsman') {
        tableName = 'craft_professions';
      } else if (businessTypeName === 'hospitality') {
        tableName = 'hospitality_categories';
      } else if (businessTypeName && businessTypeName.includes('professional')) {
        tableName = 'professionals_categories';
      } else {
        throw new Error('Invalid business type for adding profession');
      }

      // Call the server function to add the new profession
      let category: string | null = null;
      if (businessTypeName === 'hospitality') {
        category = 'Other';
      } else if (businessTypeName === 'craftsman') {
        category = 'Other'; // Default category for custom craft professions
      } else if (businessTypeName === 'tradesman') {
        // Use the selected Type A trade category when adding a new profession
        category = selectedTraderCategory || 'Other';
      } else if (businessTypeName && businessTypeName.includes('professional')) {
        // Default category for professionals vertical
        category = 'Other';
      }

      // Safety: add_profession requires category for all supported tables
      if (!category) {
        category = 'Other';
      }

      // Debug logging
      console.log('=== ADD PROFESSION DEBUG ===');
      console.log('Business type name:', businessTypeName);
      console.log('Table name:', tableName);
      console.log('Profession name:', otherProfession.trim());
      console.log('Category:', category);
      console.log('RPC parameters:', {
        p_table_name: tableName,
        p_name: otherProfession.trim(),
        p_category: category,
      });
      console.log('===========================');

      const { data, error } = await supabase.rpc('add_profession', {
        p_table_name: tableName,
        p_name: otherProfession.trim(),
        p_category: category,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned from server');

      // Update the professions list
      const newProfession = {
        id: data,
        name: otherProfession.trim(),
        is_accepted: false, // Newly added professions are not accepted by default
      };

      setProfessions(prev => [...prev, newProfession]);

      // Select the newly added profession
      setValue('profession_id', String(data));
      setShowOtherProfessionInput(false);
      setOtherProfession('');
    } catch (error) {
      console.error('Error adding profession:', error);
      setProfessionError(error instanceof Error ? error.message : 'Failed to add profession');
    } finally {
      setIsSubmittingProfession(false);
    }
  };

  // Handle avatar file selection with automatic compression
  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate the image file first
    const validation = validateImageFile(file, 20); // Allow up to 20MB before compression
    if (!validation.isValid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    // Show preview immediately from original file
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Compress the image in the background
    const originalSize = file.size;
    setIsCompressing(true);
    setCompressionInfo(`Compressing image (${formatFileSize(originalSize)})...`);

    try {
      // Use AVATAR_PRESET for profile images (max 500KB, 800px)
      const compressedFile = await compressImage(file, AVATAR_PRESET);
      const compressedSize = compressedFile.size;
      
      // Calculate reduction percentage
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(0);
      
      setAvatarFile(compressedFile);
      setCompressionInfo(
        `Image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`
      );
    } catch (err) {
      console.error('Compression failed, using original:', err);
      setAvatarFile(file); // Fall back to original file
      setCompressionInfo('Using original image');
    } finally {
      setIsCompressing(false);
    }
  };



  // Validate current step fields
  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof TraderFormData)[] = [];
    
    switch (step) {
      case 1:
        fieldsToValidate = ['store_name', 'owner_name', 'email', 'password', 'confirmPassword'];
        break;
      case 2:
        fieldsToValidate = ['business_type_id', 'profession_id', 'location'];
        break;
      case 3:
        fieldsToValidate = ['description'];
        break;
    }

    const result = await trigger(fieldsToValidate);
    
    // Additional validation for step 2
    if (step === 2) {
      // Check phone number (controlled input)
      if (!phoneNumber || phoneNumber.trim() === '') {
        setError('Phone number is required');
        return false;
      }
      
      // Validate phone number format using flexible validation
      // Accepts formats like: 087 123 4567, (087) 123 4567, 0871234567, etc.
      if (!isValidPhoneNumber(phoneNumber, countryCode)) {
        setError('Please enter a valid phone number (e.g., 087 123 4567 or (049) 952 6936)');
        return false;
      }
      
      // Normalize and format phone number for storage
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      const completePhoneNumber = countryCode + (normalizedPhone.startsWith('0') ? normalizedPhone.substring(1) : normalizedPhone);

      // Ensure a trade category is selected for tradesman registrations
      if (selectedBusinessTypeName === 'tradesman' && !selectedTraderCategory) {
        setError('Please select a trade category');
        return false;
      }
      
      // Ensure phone number is set in form
      setValue('phone_number', completePhoneNumber);
    }
    
    // Additional validation for password confirmation
    if (step === 1) {
      const { password, confirmPassword } = getValues();
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    
    setError('');
    return result;
  };

  // Handle next step
  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid && currentStep < totalSteps) {
      // Debug: Log current form values
      const currentValues = getValues();
      console.log('Form values before moving to next step:', currentValues);
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  // Defensive fetch wrapper to avoid UI hanging if a 3rd-party endpoint never responds.
  const fetchWithTimeout = async (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    timeoutMs: number
  ) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      return await fetch(input, {
        ...init,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Send admin notification email (quick hardcoded solution)
  const sendAdminNotification = async (formData: TraderFormData, traderRecord: any) => {
    const ADMIN_EMAIL = 'primo@mrprimo.org'; // Hardcoded admin email
    
    // Get business type name
    const businessType = businessTypes.find(bt => bt.id === formData.business_type_id);
    const profession = professions.find(p => p.id === parseInt(formData.profession_id));
    
    const emailData = {
      to: ADMIN_EMAIL,
      subject: `🎉 New ${businessType?.name || 'Business'} Registration - ${formData.store_name}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">New Business Registration</h2>
          
          <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #1e40af;">${formData.store_name}</h3>
            
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 8px 0; font-weight: bold; width: 120px;">Owner:</td>
                <td style="padding: 8px 0;">${formData.owner_name}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold;">Email:</td>
                <td style="padding: 8px 0;"><a href="mailto:${formData.email}">${formData.email}</a></td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold;">Phone:</td>
                <td style="padding: 8px 0;"><a href="tel:${formData.phone_number}">${formData.phone_number}</a></td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold;">Location:</td>
                <td style="padding: 8px 0;">${formData.location}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold;">Business Type:</td>
                <td style="padding: 8px 0;">${businessType?.name || 'Unknown'}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold;">Profession:</td>
                <td style="padding: 8px 0;">${profession?.name || 'Unknown'}</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; font-weight: bold;">Registered:</td>
                <td style="padding: 8px 0;">${new Date().toLocaleString()}</td>
              </tr>
            </table>
          </div>
          
          <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h4 style="margin-top: 0; color: #92400e;">Description:</h4>
            <p style="margin-bottom: 0; color: #451a03;">${formData.description}</p>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${window.location.origin}/admin/traders" 
               style="background: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
              Review in Admin Panel
            </a>
          </div>
          
          <p style="color: #6b7280; font-size: 14px; text-align: center;">
            This business registration requires your review and verification.
          </p>
        </div>
      `
    };

    // Use multiple email services for better reliability
    const emailServices = [
      {
        name: 'Netlify Forms',
        url: 'https://submit-form.com/primo-registration-notifications',
        payload: {
          subject: emailData.subject,
          message: `New Registration Alert!
          
Business: ${formData.store_name}
Owner: ${formData.owner_name}
Email: ${formData.email}
Phone: ${formData.phone_number}
Location: ${formData.location}
Type: ${businessType?.name}
Profession: ${profession?.name}
Description: ${formData.description}

Review at: ${window.location.origin}/admin/traders`,
          email: formData.email,
          name: formData.owner_name
        }
      },
      {
        name: 'Formspree',
        url: 'https://formspree.io/f/xpzvgqpj',
        payload: {
          _subject: emailData.subject,
          message: emailData.html,
          email: ADMIN_EMAIL,
          _replyto: formData.email
        }
      },
      {
        name: 'EmailJS',
        url: 'https://api.emailjs.com/api/v1.0/email/send',
        payload: {
          service_id: 'default_service',
          template_id: 'template_registration',
          user_id: 'your_user_id',
          template_params: {
            to_email: ADMIN_EMAIL,
            subject: emailData.subject,
            message: emailData.html,
            from_name: formData.owner_name,
            from_email: formData.email
          }
        }
      }
    ];

    // Try each service until one succeeds
    let emailSent = false;
    for (const service of emailServices) {
      try {
        console.log(`Attempting to send email via ${service.name}...`);
        const response = await fetchWithTimeout(service.url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(service.payload)
        }, 8000);

        console.log(`${service.name} response:`, response.status, response.statusText);
        
        if (response.ok) {
          console.log(`Admin notification sent successfully via ${service.name}`);
          emailSent = true;
          break;
        } else {
          const errorText = await response.text();
          console.warn(`${service.name} failed:`, errorText);
        }
      } catch (error) {
        console.warn(`${service.name} error:`, error);
        continue;
      }
    }

    // If all services fail, try a simple console log as fallback
    if (!emailSent) {
      console.log('=== EMAIL NOTIFICATION FALLBACK ===');
      console.log('TO:', ADMIN_EMAIL);
      console.log('SUBJECT:', emailData.subject);
      console.log('BUSINESS:', formData.store_name);
      console.log('OWNER:', formData.owner_name);
      console.log('EMAIL:', formData.email);
      console.log('PHONE:', formData.phone_number);
      console.log('LOCATION:', formData.location);
      console.log('TYPE:', businessType?.name);
      console.log('PROFESSION:', profession?.name);
      console.log('DESCRIPTION:', formData.description);
      console.log('==================================');
      
      // Don't throw error - just log it
      console.warn('All email services failed, but registration completed successfully');
    }
  };

  // Handle form submission
  const onSubmit = async (data: TraderFormData) => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      // Determine auth role based on selected business type
      let authRole: 'trader' | 'craftsman' | 'hospitality' | 'professional' = 'trader';
      if (selectedBusinessTypeName === 'craftsman') {
        authRole = 'craftsman';
      } else if (selectedBusinessTypeName === 'hospitality') {
        authRole = 'hospitality';
      } else if (selectedBusinessTypeName && selectedBusinessTypeName.includes('professional')) {
        authRole = 'professional';
      }

      // Step 1: Sign up the user with Supabase Auth
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            role: authRole,
            display_name: data.owner_name,
            full_name: data.owner_name,
            store_name: data.store_name
          }
        }
      });

      console.log('Signup response:', { authData, signUpError });

      if (signUpError || !authData.user) {
        throw signUpError || new Error('Failed to create user account');
      }

      let avatarUrl = '';
      // If we have an avatar, upload it and get the URL
      if (avatarFile && authData.user) {
        try {
          avatarUrl = await traderService.uploadAvatar(authData.user.id, avatarFile);
          console.log('Avatar uploaded successfully, URL:', avatarUrl);
        } catch (uploadError) {
          console.error('Avatar upload failed:', uploadError);
          // Decide if you want to stop the process or continue without an avatar
        }
      }

      // Step 3: Create trader profile using the working traderService method
      console.log('Creating trader profile with data:', {
        user_id: authData.user.id,
        email: data.email,
        store_name: data.store_name,
        owner_name: data.owner_name,
        phone_number: data.phone_number,
        location: data.location,
        description: data.description,
        profession_id: data.profession_id || '1', // Use string as per service interface
        avatar_url: avatarUrl,
        website: data.website,
      });

      // Skip users table (RLS policy blocks it) and go directly to traders table
      console.log('Step 3: Insert into traders table (skipping users table due to RLS policy)');
      
      const traderData = {
        user_id: authData.user.id,
        email: data.email,
        store_name: data.store_name,
        owner_name: data.owner_name,
        phone_number: data.phone_number,
        location: data.location,
        description: data.description,
      };
      
      console.log('Trader data to insert:', traderData);
      
      // Skip RPC and go directly to insert with explicit casting
    console.log('Attempting direct insert with explicit type casting...');
    
    // Determine the correct table based on business type
    let tableName = 'traders'; // default for tradesman
    if (selectedBusinessTypeName === 'craftsman') {
      tableName = 'crafties';
    } else if (selectedBusinessTypeName === 'hospitality') {
      tableName = 'hospitality';
    } else if (selectedBusinessTypeName && selectedBusinessTypeName.includes('professional')) {
      tableName = 'professionals';
    }
    
    // Prepare data based on table - only traders, crafties, hospitality and professionals tables have slug
    const baseData = {
      user_id: authData.user.id, // Explicitly keep as UUID
      email: String(data.email),
      store_name: String(data.store_name),
      owner_name: String(data.owner_name),
      phone_number: String(data.phone_number),
      location: String(data.location),
      description: String(data.description),
      avatar_url: avatarUrl,
      profession_id: parseInt(data.profession_id, 10),
      website: data.website ? normalizeWebsiteUrl(data.website) : null, // Normalize website URL for storage
    };

    let insertData: typeof baseData | (typeof baseData & { business_type_id?: string; slug: string });

    if (tableName === 'traders') {
      const slug = await traderService.generateUniqueSlug(data.store_name);
      insertData = {
        ...baseData,
        // Use the selected business type state as the primary source of truth
        business_type_id: selectedBusinessType || data.business_type_id,
        slug,
      };
    } else if (tableName === 'crafties') {
      const slug = await craftsmanService.generateUniqueSlug(data.store_name);
      insertData = {
        ...baseData,
        slug,
      };
    } else if (tableName === 'hospitality') {
      const slug = await generateUniqueHospitalitySlug(data.store_name);
      insertData = {
        ...baseData,
        // Hospitality table also tracks business_type_id (should be the Hospitality type)
        business_type_id: selectedBusinessType || data.business_type_id,
        slug,
      };
    } else if (tableName === 'professionals') {
      const slug = await generateUniqueProfessionalSlug(data.store_name);
      insertData = {
        ...baseData,
        // Professionals table tracks business_type_id as well
        business_type_id: selectedBusinessType || data.business_type_id,
        slug,
      };
    } else {
      insertData = baseData;
    }
    
    // Debug logging
    console.log('=== REGISTRATION DEBUG ===');
    console.log('Form data received:', data);
    console.log('Selected business type ID:', data.business_type_id);
    console.log('Selected profession ID:', data.profession_id);
    console.log('Business types available:', businessTypes);
    console.log('Professions available:', professions);
    console.log('Selected business type name:', selectedBusinessTypeName);
    console.log('Data being inserted to database:', insertData);
    console.log('========================');
    
    console.log(`Inserting into table: ${tableName}`);
    
    console.log('=== TRADER INSERT DEBUG ===');
    console.log('Table name:', tableName);
    console.log('Insert data:', insertData);
    console.log('============================');
    
    const { data: insertedTrader, error: traderError } = await supabase
      .from(tableName)
      .insert(insertData)
      .select();
      
    console.log('=== DETAILED INSERT RESULT ===');
    console.log('insertedTrader:', insertedTrader);
    console.log('traderError:', traderError);
    console.log('traderError type:', typeof traderError);
    console.log('traderError keys:', traderError ? Object.keys(traderError) : 'null');
    console.log('traderError.message:', traderError?.message);
    console.log('traderError.details:', traderError?.details);
    console.log('traderError.hint:', traderError?.hint);
    console.log('traderError.code:', traderError?.code);
    console.log('================================');
      
      // If we still get the uuid = integer error, it's likely a database constraint issue
      if (traderError && traderError.message && traderError.message.includes('uuid = integer')) {
        console.error('UUID = INTEGER ERROR ANALYSIS:');
        console.error('This error suggests a database constraint, trigger, or foreign key is comparing UUID to INTEGER');
        console.error('Possible causes:');
        console.error('1. A foreign key constraint expecting integer but receiving UUID');
        console.error('2. A database trigger with incorrect type comparison');
        console.error('3. A check constraint comparing different types');
        console.error('4. RLS policy with type mismatch');
        
        // Let's try to identify which field is causing the issue by checking the database schema
        console.error('Data being inserted:', traderData);
        console.error('All fields are strings except user_id which is UUID');
        
        throw new Error(`Database constraint error: ${traderError.message}. This appears to be a database schema or constraint issue, not a code issue.`);
      }

      if (traderError) {
        console.error('Error creating trader profile:', traderError);
        throw new Error(`Failed to create trader profile: ${traderError.message || 'Unknown error'}`);
      }

      if (!insertedTrader || insertedTrader.length === 0) {
        console.error('No trader data returned after insert');
        throw new Error('Trader profile was not created successfully.');
      }

      console.log('Trader profile created successfully:', insertedTrader[0]);

    // Trigger keyword generation (non-blocking)
    const traderRecord = insertedTrader[0];
    const businessType = getBusinessTypeFromTable(tableName);
    triggerKeywordGeneration(traderRecord.id, businessType, {
      store_name: traderRecord.store_name,
      description: traderRecord.description,
      location: traderRecord.location
    });

    // Send admin notification email (non-blocking so it cannot stall the UI)
    void sendAdminNotification(data, insertedTrader[0]).catch((emailError) => {
      console.error('Failed to send admin notification:', emailError);
    });

    setSuccess(true);
    } catch (error: any) {
      setError(error.message || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div key="step1" className="space-y-4">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name *
              </label>
              <div
                className="relative"
                onFocus={() => setFocusedField('store_name')}
                onBlur={() => setFocusedField(null)}
              >
                <input
                  type="text"
                  id="store_name"
                  {...register('store_name', { required: 'Company name is required' })}
                  className="w-full px-3 py-2 pr-10 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder="Enter your company name"
                  autoComplete="organization"
                />
                {isFieldValid('store_name') && (
                  <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-green-500 animate-pulse">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              {errors.store_name && (
                <p className="mt-1 text-sm text-red-500">{errors.store_name.message}</p>
              )}
            </div>

            {/* Owner Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner Name *
              </label>
              <div
                className="relative"
                onFocus={() => setFocusedField('owner_name')}
                onBlur={() => setFocusedField(null)}
              >
                <input
                  type="text"
                  id="owner_name"
                  {...register('owner_name', { required: 'Owner name is required' })}
                  className="w-full px-3 py-2 pr-10 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder="Enter owner's full name"
                  autoComplete="name"
                />
                {isFieldValid('owner_name') && (
                  <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-green-500 animate-pulse">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              {errors.owner_name && (
                <p className="mt-1 text-sm text-red-500">{errors.owner_name.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div
                className="relative"
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
              >
                <input
                  type="email"
                  id="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className="w-full px-3 py-2 pr-10 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder="Enter your email address"
                  autoComplete="email"
                />
                {isFieldValid('email') && (
                  <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-green-500 animate-pulse">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <div
                className="relative"
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
              >
                <input
                  type="password"
                  id="password"
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: PASSWORD_MIN_LENGTH,
                      message: 'Password must be at least ' + PASSWORD_MIN_LENGTH + ' characters'
                    }
                  })}
                  className="w-full px-3 py-2 pr-10 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder="Create a password"
                  autoComplete="off"
                />
                {isFieldValid('password') && (
                  <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-green-500 animate-pulse">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <div
                className="relative"
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
              >
                <input
                  type="password"
                  id="confirmPassword"
                  {...register('confirmPassword', { required: 'Please confirm your password' })}
                  className="w-full px-3 py-2 pr-10 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder="Confirm your password"
                  autoComplete="off"
                />
                {isFieldValid('confirmPassword') && (
                  <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-green-500 animate-pulse">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div key="step2" className="space-y-4">
            {/* Business Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Type *
              </label>
              <select
                id="business_type_id"
                {...register('business_type_id', { 
                  required: 'Business type is required',
                  onChange: (e) => handleBusinessTypeChange(e.target.value)
                })}
                className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
              >
                <option value="">Select business type</option>
                {businessTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.business_type_id && (
                <p className="mt-1 text-sm text-red-500">{errors.business_type_id.message}</p>
              )}
            </div>

            {/* Trade Category (Type A) - only for tradesman */}
            {selectedBusinessTypeName === 'tradesman' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trade Category *
                </label>
                <select
                  value={selectedTraderCategory}
                  onChange={(e) => handleTraderCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                >
                  <option value="">Select trade category</option>
                  {traderCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Profession (only show if professions are available) */}
            {professions.length > 0 && (
              <div className="mb-4">
                <label htmlFor="profession_id" className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedBusinessTypeName === 'hospitality' ? 'Hospitality Category' : 'Profession'}
                </label>
                <select
                  id="profession_id"
                  {...register('profession_id', {
                    required: 'This field is required',
                    onChange: handleProfessionChange
                  })}
                  className={`w-full p-2 border rounded ${errors.profession_id ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={
                    !selectedBusinessType ||
                    (selectedBusinessTypeName === 'tradesman' && !selectedTraderCategory)
                  }
                >
                  <option value="">
                    {selectedBusinessTypeName === 'hospitality' 
                      ? 'Select a hospitality category' 
                      : 'Select a profession'}
                  </option>
                  {professions.map((profession) => (
                    <option key={profession.id} value={profession.id}>
                      {profession.name}
                    </option>
                  ))}
                  <option value="other">
                    Other (please specify)
                  </option>
                </select>
                
                {showOtherProfessionInput && (
                  <div className="mt-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={otherProfession}
                        onChange={(e) => setOtherProfession(e.target.value)}
                        placeholder={`Enter ${selectedBusinessTypeName === 'hospitality' ? 'category' : 'profession'} name`}
                        className="flex-1 p-2 border rounded"
                        disabled={isSubmittingProfession}
                      />
                      <button
                        type="button"
                        onClick={handleAddNewProfession}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                        disabled={isSubmittingProfession || !otherProfession.trim()}
                      >
                        {isSubmittingProfession ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                    {professionError && (
                      <p className="mt-1 text-sm text-red-600">{professionError}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Your submission will be reviewed by an administrator.
                    </p>
                  </div>
                )}
                {errors.profession_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.profession_id.message}</p>
                )}
              </div>
            )}

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="location"
                  {...register('location', { required: 'Location is required' })}
                  onFocus={() => setFocusedField('location')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-3 py-2 pr-10 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder="Enter your business location"
                  autoComplete="off"
                />
                {isFieldValid('location') && (
                  <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-green-500 animate-pulse">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              {errors.location && (
                <p className="mt-1 text-sm text-red-500">{errors.location.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <div className="flex items-stretch space-x-2">
                {/* Country Code Selector */}
                <select
                  value={countryCode}
                  onChange={(e) => {
                    const newCountryCode = e.target.value;
                    setCountryCode(newCountryCode);
                    
                    // Update the complete phone number when country code changes
                    if (phoneNumber) {
                      let formattedNumber = phoneNumber;
                      if (phoneNumber.startsWith('0')) {
                        formattedNumber = phoneNumber.substring(1);
                      }
                      const completeNumber = newCountryCode + formattedNumber;
                      setValue('phone_number', completeNumber);
                    }
                  }}
                  className="w-28 px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                >
                  <option value="+353">🇮🇪 +353</option>
                  <option value="+44">🇬🇧 +44</option>
                </select>
                
                {/* Phone Number Input */}
                <input
                  type="tel"
                  id="phone_number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPhoneNumber(value);
                    
                    // Format and validate the complete phone number
                    let formattedNumber = value;
                    
                    // Remove leading zero if present (common in Irish/UK numbers)
                    if (value.startsWith('0')) {
                      formattedNumber = value.substring(1);
                    }
                    
                    // Set the complete phone number in the form
                    const completeNumber = countryCode + formattedNumber;
                    setValue('phone_number', completeNumber);
                  }}
                  className="flex-1 min-w-0 px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                  placeholder={countryCode === '+353' ? '87 123 4567' : '7700 900123'}
                  autoComplete="tel"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {countryCode === '+353' 
                  ? 'Irish format: Enter your number with or without the leading 0 (e.g., 087 123 4567 or 87 123 4567)'
                  : 'UK format: Enter your number with or without the leading 0 (e.g., 07700 900123 or 7700 900123)'
                }
              </p>
              {errors.phone_number && (
                <p className="mt-1 text-sm text-red-500">{errors.phone_number.message}</p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div key="step3" className="space-y-4">
            {/* Company Photo/Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Photo/Logo
              </label>
              <div className="mt-1 flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  id="avatar-upload"
                  disabled={isCompressing}
                />
                <label
                  htmlFor="avatar-upload"
                  className={`cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isCompressing ? 'opacity-50 cursor-wait' : ''}`}
                >
                  {isCompressing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Optimizing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photo
                    </>
                  )}
                </label>
                {avatarPreview && (
                  <img
                    src={avatarPreview}
                    alt="Company logo preview"
                    className="h-16 w-16 rounded-lg object-cover border-2 border-gray-200"
                  />
                )}
              </div>
              {/* Show compression status/result */}
              {compressionInfo && (
                <p className="mt-1 text-xs text-green-600">
                  ✓ {compressionInfo}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Upload your company logo or a representative image (optional). Large images will be automatically optimized.
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Description *
              </label>
              <textarea
                id="description"
                {...register('description', { 
                  required: 'Company description is required',
                  minLength: {
                    value: 20,
                    message: 'Description must be at least 20 characters'
                  }
                })}
                className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                rows={4}
                placeholder="Describe your business, products, or services..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Website (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website (Optional)
              </label>
              <input
                type="text"
                id="website"
                {...register('website', {
                  validate: validateWebsite
                })}
                className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-white/50"
                placeholder="example.com or www.example.com"
                autoComplete="url"
              />
              {errors.website && (
                <p className="mt-1 text-sm text-red-500">{errors.website.message}</p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundImage: "url('/traders-small-size.jpg')", backgroundAttachment: 'fixed' }}>
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="bg-white/30 backdrop-blur-md p-8 rounded-2xl shadow-lg border border-white/30">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for registering with Ballyconnell Marketplace. Please check your email to verify your account.
            </p>
            <p className="text-sm text-gray-500">
              Once verified, you can login to the dashboard and manage your data, add features, manage your social media links.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundImage: "url('/traders-small-size.jpg')", backgroundAttachment: 'fixed' }}>
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white/25 backdrop-blur-md p-8 rounded-2xl shadow-lg border border-white/30">
          <StepIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
            title={stepTitles[currentStep - 1]}
          />

          {error && (
            <div className="bg-red-50 text-red-500 p-3 rounded-md mb-4 text-sm">
              {error}
            </div>
          )}

          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            {renderStepContent()}

            <div className="flex justify-between mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </button>
              )}

              <div className="ml-auto">
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      'Complete Registration'
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
